import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { NgForm } from '@angular/forms';
import { Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import { filter, Subject, Subscription, takeUntil } from 'rxjs';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { EventMessage, EventType, InteractionStatus } from '@azure/msal-browser';
import { AuthService } from 'src/app/core/services/auth.service';
import { UserRoleService } from 'src/app/core/services/user-role.service';
import {
  ASSETS,
  AUTH_MESSAGES,
  AUTH_STRINGS,
  ROUTER_LINKS,
} from '../../constants/app-strings.constants';
import { LoginResponse } from '../../models/login-response.model';

@Component({
  selector: 'app-auth',
  templateUrl: './auth.component.html',
  styleUrl: './auth.component.scss',
})
export class AuthComponent implements OnInit, OnDestroy {
  @ViewChild('loginForm') form: NgForm | undefined;
  ASSETS = ASSETS;
  AUTH_STRINGS = AUTH_STRINGS;
  AUTH_MESSAGES = AUTH_MESSAGES;
  loading: boolean = false;
  loginSubscription$: Subscription | undefined;
  private readonly _destroying$ = new Subject<null>();

  constructor(
    private authService: AuthService,
    private messageService: MessageService,
    private router: Router,
    private msalService: MsalService,
    private msalBroadcastService: MsalBroadcastService,
    private userRoleService: UserRoleService
  ) {}

  ngOnInit(): void {
 
    this.msalService.handleRedirectObservable().subscribe({
      next: (result) => {
        if (!result) {
          this.loading = false;
        }
      },
      error: () => {
        this.loading = false;
      }
    });

    this.msalBroadcastService.msalSubject$
    .pipe(
      filter((msg: EventMessage) => msg.eventType === EventType.ACCOUNT_ADDED || msg.eventType === EventType.ACCOUNT_REMOVED),
    )
    .subscribe((result: EventMessage) => {
      if (this.msalService.instance.getAllAccounts().length === 0) {
        this.router.navigate(['/login']);
      } else {
        this.setLoginDisplay();
      }
    });

    this.msalBroadcastService.inProgress$
    .pipe(
      filter((status: InteractionStatus) => status === InteractionStatus.None),
      takeUntil(this._destroying$)
    )
    .subscribe(() => {
      this.setLoginDisplay();
    })
  }

  login(): void {
    this.loading = true;
    const loginCredentials = this.form?.value;
    this.loginSubscription$ = this.authService
      .login(loginCredentials)
      .subscribe({
        next: (loginResponse: LoginResponse) => {
          this.loading = false;
          this.messageService.add({
            severity: 'success',
            detail: this.AUTH_MESSAGES.loginSuccessful,
          });
          sessionStorage.setItem(AUTH_STRINGS.idtoken, loginResponse.idtoken);
          sessionStorage.setItem(AUTH_STRINGS.usertoken, JSON.stringify(loginResponse.WGINB_WG_ASSESSMENT_PORTAL_USER));
          this.authService.loginLogoutStatus$.next();
          this.navigateAfterLogin();
        },
        error: (error) => {
          this.messageService.add({
            severity: 'error',
            detail: this.AUTH_MESSAGES.invalidCredentials,
          });
          this.loading = false;
        },
      });
  }

  onSsoLogin(): void {
    this.loading = true;
    try {
      this.authService.loginSso();
      this.loading = false;
    } catch (error) {
      this.loading = false;
    }
  }

  async setLoginDisplay(): Promise<void> {
    const loginStatus = this.msalService.instance.getAllAccounts().length > 0;

    if(loginStatus){
      const profile_data = this.msalService.instance.getAllAccounts()[0];
      this.msalService.instance.setActiveAccount(profile_data);

      try {
        if (!this.authService.isMsalLoggedIn()) {
          this.setMsalToken();
        }
        else {
          this.loginUsingSso();
        }
      } catch (error) {
        this.loading = false;
      }
    } else {
      if(this.authService.isMsalLoggedIn()) {
        await this.onSsoLogin();

        this.loading = false;
      }
      this.router.navigate(['/login']);
    }
  }

  setMsalToken(): void {
    const accounts = this.msalService.instance.getAllAccounts();
    if (accounts.length === 0) {
      this.loading = false;
      return;
    }
    const tokenRequest = {
      scopes: ["user.read"],
      account: accounts[0]
    };
    this.msalService.acquireTokenSilent(tokenRequest).subscribe({
      next: (tokenResponse) => {
        if (!tokenResponse?.accessToken || !tokenResponse?.idToken) {
          this.loading = false;
          return;
        }
        const expiry = new Date((new Date()).getTime() + 3600 * 1000);
        // Store MSAL tokens in sessionStorage to prevent RequestHeaderSectionTooLarge (8KB cookie limit)
        sessionStorage.setItem(
          'msal-access-token',
          JSON.stringify({ token: tokenResponse.accessToken, expiry })
        );
        sessionStorage.setItem(
          'msal-id-token',
          JSON.stringify({ token: tokenResponse.idToken, expiry })
        );
        this.loginUsingSso();
      },
      error: () => {
        this.loading = false;
      },
    });
  }

  loginUsingSso(): void {
    const msalAccessToken = sessionStorage.getItem('msal-access-token');
    const msalIdToken = sessionStorage.getItem('msal-id-token');
    if (!msalAccessToken || !msalIdToken) {
      this.loading = false;
      return;
    }
    try {
      const accessTokenData = JSON.parse(msalAccessToken);
      const idTokenData = JSON.parse(msalIdToken);
      if (!accessTokenData?.token || !idTokenData?.token) {
        this.loading = false;
        return;
      }
      const activeAccount = this.msalService.instance.getActiveAccount();
      if (!activeAccount?.username) {
        this.loading = false;
        return;
      }
      this.authService.loginUsingSso({
        accessToken: accessTokenData.token,
        idToken: idTokenData.token,
        email: activeAccount.username
      }).subscribe({
        next: (loginResponse: LoginResponse) => {
          this.loading = false;
          this.messageService.add({
            severity: 'success',
            detail: this.AUTH_MESSAGES.loginSuccessful,
          });
          sessionStorage.setItem(AUTH_STRINGS.idtoken, loginResponse.idtoken);
          sessionStorage.setItem(AUTH_STRINGS.usertoken, JSON.stringify(loginResponse.WGINB_WG_ASSESSMENT_PORTAL_USER));
          this.authService.loginLogoutStatus$.next();
          this.navigateAfterLogin();
        },
        error: () => {
          this.messageService.add({
            severity: 'error',
            detail: this.AUTH_MESSAGES.invalidCredentials,
          });
          this.loading = false;
        },
      });
    } catch {
      this.loading = false;
    }
  }

  private navigateAfterLogin(): void {
    const role = this.userRoleService.getUserRole().trim().toLowerCase();

    if (role === 'admin') {
      this.router.navigateByUrl(ROUTER_LINKS.library);
      return;
    }

    this.router.navigateByUrl(ROUTER_LINKS.test);
  }

  private getDomainFromURL(url: string): string {
    const parts = url.split('.');
    if(parts.length > 2) 
      return parts.slice(-2).join('.');
    else
      return parts[0];
  }

  ngOnDestroy(): void {
    this.loginSubscription$?.unsubscribe();
    this._destroying$.next(null);
    this._destroying$.complete();
  }
}
